import React, { useMemo, useCallback, useState } from 'react';
import { AgGridReact } from 'ag-grid-react';
import type { ColDef, ICellRendererParams } from 'ag-grid-community';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import { Accordion, AccordionItem, Tooltip, Spinner } from '@nextui-org/react';
import { Icon } from '@iconify/react';
import { useTranslation } from 'react-i18next';
import { parseScore } from '../utils/dateutils';
import { downloadFileFromUrl } from '../utils/conversationDownloadUtils';
import { renderMap } from './claimScoreGeoMap';
import {
    SourceJsonModal,
    type SourceJsonModalState,
    filterCkDataForSource,
    mapSourceValueToModalTitle,
} from './SourceJsonModal';
import {
    ACCORDION_TITLE_TEXT_CLASS,
    BOX_ACCORDION_CLASS_NAMES,
    HIDDEN_ACCORDION_DIVIDER_PROPS,
    LINE_ACCORDION_CLASS_NAMES,
    V2_NESTED_LIGHT_ACCORDION_CLASS,
    getScoreColorFromFormat,
    renderClaimScoreFormattedField,
    ContextScoreChip,
    type ScoreColorName,
} from './claimScoreRendererShared';
import {
    ActionOutputV2Payload,
    V2_IGNORED_NODE_KEYS,
    V2_ACCORDION_CONTENT_CLASS,
    V2_NESTED_SECTION_CLASS,
    V2_REQUESTS_SECTION_KEY,
    V2SourceId,
    V2ScoredNode,
    buildV2TopLevelOrderedEntries,
    collectSearchableText,
    expandNumericKeySlots,
    formatSectionTitle,
    SliceSectionTitle,
    isMediaSourceKey,
    isMediaAttachmentObject,
    resolveNodeTitle,
    getFieldFormat,
    getScoredNodeExplanation,
    getV2SectionFieldRecord,
    getV2NodeDisplayScore,
    getV2NodeOwnScore,
    hasNestedScoredChildren,
    hasOnlyNumericExplanationKeys,
    isNumericExplanationKey,
    isNumericFieldValue,
    segmentMatchesKey,
    isTopLevelBodyAccordionChild,
    isScoredNode,
    isV2NonPrimitiveValue,
    isV2PrimitiveValue,
    isV2ScoreFieldKey,
    pickBoxAccordionIcon,
    resolveOrderedKeys,
    getV2SourcesKnowledgeIds,
    getV2SourcesTemplateData,
    stripFieldSuffix,
    textMatchesSearch,
    type V2TopLevelSectionValue,
} from './actionOutputV2Utils';
import {
    AttachmentMediaWithFallback,
    collectAttachmentSourcesFromValue,
    inferMediaTypeFromFieldKey,
    isAudioExtension,
    isVideoExtension,
    normalizeAttachmentItems,
    useV2AttachmentPresignedUrls,
    type AttachmentSourcePayload,
} from './claimScoreAttachmentMedia';
/** @table cells longer than this wrap and auto-grow their row height instead of clipping. */
const TABLE_WRAP_TEXT_MIN_LENGTH = 15;

const GRID_DEFAULT_COL_DEF: ColDef = {
    sortable: false,
    resizable: true,
    suppressMovable: true,
    headerClass: 'text-left font-medium text-default-700',
};

const FIELD_VALUE_CLASS = 'renderer-field-value text-default-500 mb-1';
const LEAF_VALUE_CLASS = 'renderer-field-value text-default-500';
const V2_COLLAPSE_ACCORDION_CLASS = 'gap-2 [&_hr]:hidden [&_.divider]:hidden !px-0';
const V2_SPLITTED_ACCORDION_CLASS = 'gap-2 [&_hr]:hidden [&_.divider]:hidden';
const LINE_ACCORDION_ITEM_CLASS = '[&>h2>button]:block';
const ATTACHMENT_STATUS_BOX_CLASS =
    'flex items-center justify-center rounded-lg border border-default-200 min-h-[120px]';

function readGeoCoordinate(value: unknown): number | null {
    if (typeof value === 'number' && Number.isFinite(value)) return value;
    if (typeof value === 'string' && value.trim() !== '') {
        const parsed = Number(value);
        if (Number.isFinite(parsed)) return parsed;
    }
    return null;
}

function readGeoPoint(value: object): { lat: number; lng: number } | null {
    const record = value as Record<string, unknown>;
    let lat: number | null = null;
    let lng: number | null = null;
    for (const [key, entry] of Object.entries(record)) {
        const name = stripFieldSuffix(key).toLowerCase();
        if (name === 'latitude') lat = readGeoCoordinate(entry);
        if (name === 'longitude') lng = readGeoCoordinate(entry);
    }
    if (lat == null || lng == null) return null;
    return { lat, lng };
}

function isGeoAxisKey(key: string): boolean {
    const name = stripFieldSuffix(key).toLowerCase();
    return name === 'latitude' || name === 'longitude';
}

const renderKeyValueField = (
    fieldKey: string,
    label: string,
    content: React.ReactNode,
    valueClassName: string = FIELD_VALUE_CLASS
): React.ReactElement => (
    <div key={fieldKey} id={fieldKey} className="renderer-key-value-block">
        <div className="renderer-field-name capitalize font-medium text-default-700 mb-1">{label}</div>
        <div className={valueClassName}>{content}</div>
    </div>
);

const INFO_SUFFIX = '@info';

const isReactElement = (el: unknown): el is React.ReactElement => el != null;

/** Collect all present keys from table rows. */
function collectTablePresentKeys(rows: Record<string, unknown>[]): Set<string> {
    const keys = new Set<string>();
    for (const row of rows) {
        if (row && typeof row === 'object') {
            for (const k of Object.keys(row)) keys.add(k);
        }
    }
    return keys;
}

/** Detect columns that have meaningful @info companion data. */
function detectInfoColumns(
    presentKeys: Set<string>,
    rows: Record<string, unknown>[]
): Set<string> {
    const columnsWithInfo = new Set<string>();
    for (const k of presentKeys) {
        if (!k.endsWith(INFO_SUFFIX)) continue;
        const baseKey = k.slice(0, -INFO_SUFFIX.length);
        if (!presentKeys.has(baseKey)) continue;
        const anyRowHasInfo = rows.some(
            (row) => row && typeof row === 'object' && hasMeaningfulInfo(row[k])
        );
        if (anyRowHasInfo) columnsWithInfo.add(baseKey);
    }
    return columnsWithInfo;
}

/** Build ordered column list from order array + remaining present keys. */
function buildOrderedColumns(
    presentKeys: Set<string>,
    order: string[] | undefined
): string[] {
    const orderedColumns: string[] = [];
    const seen = new Set<string>();
    for (const col of order ?? []) {
        if (col.endsWith(INFO_SUFFIX)) continue;
        if (presentKeys.has(col) && !seen.has(col)) {
            orderedColumns.push(col);
            seen.add(col);
        }
    }
    for (const col of presentKeys) {
        if (col.endsWith(INFO_SUFFIX)) continue;
        if (!seen.has(col)) orderedColumns.push(col);
    }
    return orderedColumns;
}

/** A score renders as a chip when it is numeric or a non-empty string (shown verbatim). */
const isScoreDisplayValue = (value: unknown): boolean =>
    isNumericFieldValue(value) || (typeof value === 'string' && value.trim() !== '');

/** Label + score chip row. `explicitColor` comes from a `@success`/`@warning`/`@danger` key suffix. */
const renderScoreRow = (
    fieldKey: string,
    label: string,
    value: unknown,
    explicitColor: ScoreColorName | null
): React.ReactElement => (
    <div key={fieldKey} id={fieldKey} className="flex items-center justify-between gap-2 py-1 mb-1">
        <span className="renderer-field-name capitalize font-medium text-default-700">{label}</span>
        <ContextScoreChip score={value} explicitColor={explicitColor} />
    </div>
);

const resolveNodeHeaderScore = (
    node: V2ScoredNode,
    explanation?: Record<string, unknown>
): { score: unknown; color: ScoreColorName | null } => {
    const ownScore = getV2NodeOwnScore(node);
    return {
        score: ownScore.value ?? (explanation ? getV2NodeDisplayScore(node, explanation) : undefined),
        color: getScoreColorFromFormat(ownScore.format),
    };
};

const renderHeaderScoreChip = (score: unknown, color: ScoreColorName | null): React.ReactNode =>
    isScoreDisplayValue(score) ? <ContextScoreChip score={score} explicitColor={color} /> : null;

/** True when value is meaningful (non-empty array/object/scalar). */
const hasMeaningfulInfo = (value: unknown): boolean => {
    if (value == null || value === '') return false;
    if (Array.isArray(value)) return value.length > 0;
    if (typeof value === 'object') return Object.keys(value).length > 0;
    return true;
};

type V2GridContext = {
    setSourceJsonModal: (state: SourceJsonModalState | null) => void;
    columnsWithInfo: Set<string>;
    viewLabel: string;
    /** Columns whose cells should wrap (autoHeight) instead of truncating with ellipsis. */
    wrapColumns: Set<string>;
};

const V2InfoCellRenderer: React.FC<ICellRendererParams> = (props) => {
    const ctx = props.context as V2GridContext | undefined;
    const colKey = props.colDef?.field;
    const cellValue = props.value;
    let display = '—';
    if (cellValue != null) {
        display = typeof cellValue === 'string' ? formatSectionTitle(cellValue) : String(cellValue);
    }
    const shouldWrap = !!colKey && !!ctx?.wrapColumns.has(colKey);
    const textOverflowClass = shouldWrap ? 'whitespace-normal break-words' : 'truncate';
    if (!ctx || !colKey || !ctx.columnsWithInfo.has(colKey)) {
        return <span className={`capitalize text-default-500 ${textOverflowClass}`}>{display}</span>;
    }
    const row = props.data as Record<string, unknown> | undefined;
    const infoData = row ? row[`${colKey}${INFO_SUFFIX}`] : undefined;
    const showInfo = hasMeaningfulInfo(infoData);
    const onClick = () => {
        const title = mapSourceValueToModalTitle(cellValue, display);
        let modalData: unknown = infoData;
        if (title === 'C_&_K_source' && Array.isArray(infoData)) {
            modalData = filterCkDataForSource(infoData, String(cellValue ?? ''));
        }
        ctx.setSourceJsonModal({ title, data: modalData });
    };
    return (
        <span className="inline-flex items-center gap-1 capitalize text-default-500 max-w-full">
            <span className={`${textOverflowClass} min-w-0`}>{display}</span>
            {showInfo && (
                <Tooltip content={`${ctx.viewLabel} ${display}`} placement="top" showArrow>
                    <button
                        type="button"
                        onClick={onClick}
                        className="inline-flex items-center justify-center rounded-full focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-1 text-default-500 hover:text-default-700"
                        aria-label={`${ctx.viewLabel} ${display}`}
                    >
                        <Icon icon="mdi:information-outline" className="w-4 h-4" />
                    </button>
                </Tooltip>
            )}
        </span>
    );
};

const LineAccordionTitle: React.FC<{
    title: string;
    score?: unknown;
    scoreColor?: ScoreColorName | null;
}> = ({ title, score, scoreColor = null }) => (
    <div className="flex items-center w-full min-w-0 max-w-full overflow-hidden gap-2">
        <div className="flex items-center gap-1 min-w-0 shrink overflow-hidden">
            <span className={`${ACCORDION_TITLE_TEXT_CLASS} text-default-500 text-[14px]`}>{title}</span>
            <Icon
                icon="mdi:chevron-down"
                className="text-default-500 w-4 h-4 shrink-0 flex-none transition-transform group-aria-expanded:rotate-180"
            />
        </div>
        <div className="flex-1 min-w-2 border-b border-default-300 self-center" />
        <div className="shrink-0 flex-none">{renderHeaderScoreChip(score, scoreColor)}</div>
    </div>
);

const BoxAccordionTitleRow: React.FC<{
    displayTitle: string;
    subtitle?: string;
    thumbnail: React.ReactNode;
    headerScore?: unknown;
    headerScoreColor?: ScoreColorName | null;
    showScore: boolean;
}> = ({ displayTitle, subtitle, thumbnail, headerScore, headerScoreColor, showScore }) => (
    <div className="flex items-center w-full min-w-0 max-w-full overflow-hidden gap-2">
        <div className="flex items-center gap-3 min-w-0 flex-1 basis-0 overflow-hidden">
            {thumbnail}
            <div className="min-w-0 flex-1 overflow-hidden">
                <div className={`renderer-text-primary font-semibold ${ACCORDION_TITLE_TEXT_CLASS}`}>
                    {displayTitle}
                </div>
                {subtitle ? (
                    <div className={`renderer-text-secondary ${ACCORDION_TITLE_TEXT_CLASS}`}>{subtitle}</div>
                ) : null}
            </div>
        </div>
        {showScore ? (
            <div className="shrink-0 flex-none">{renderHeaderScoreChip(headerScore, headerScoreColor ?? null)}</div>
        ) : null}
    </div>
);

const LineAccordionShell: React.FC<{
    itemKey: string;
    title: string;
    body: React.ReactNode;
    score?: unknown;
    scoreColor?: ScoreColorName | null;
    contentClassName?: string;
    selectedKeys: Set<string>;
    onSelectionChange: (keys: 'all' | Set<React.Key>) => void;
}> = ({ itemKey, title, body, score, scoreColor = null, contentClassName = 'pr-0', selectedKeys, onSelectionChange }) => (
    <div key={itemKey} className="mb-2">
        <Accordion
            variant="light"
            isCompact
            selectionMode="multiple"
            selectedKeys={selectedKeys}
            onSelectionChange={onSelectionChange}
            className={V2_COLLAPSE_ACCORDION_CLASS}
        >
            <AccordionItem
                key={itemKey}
                aria-label={title}
                title={<LineAccordionTitle title={title} score={score} scoreColor={scoreColor} />}
                indicator={<></>}
                classNames={{ ...LINE_ACCORDION_CLASS_NAMES, content: contentClassName }}
                className={LINE_ACCORDION_ITEM_CLASS}
            >
                <div className={`${V2_NESTED_SECTION_CLASS} pt-1`}>{body}</div>
            </AccordionItem>
        </Accordion>
    </div>
);

const createLineAccordionItem = (
    itemKey: string,
    title: string,
    children: React.ReactNode,
    score?: unknown,
    scoreColor?: ScoreColorName | null
): React.ReactElement => (
    <AccordionItem
        key={itemKey}
        aria-label={title}
        title={<LineAccordionTitle title={title} score={score} scoreColor={scoreColor} />}
        indicator={<></>}
        classNames={LINE_ACCORDION_CLASS_NAMES}
        className={LINE_ACCORDION_ITEM_CLASS}
    >
        {children}
    </AccordionItem>
);

const HtmlAttachmentDownload: React.FC<{ url: string; filename: string }> = ({ url, filename }) => {
    const { t } = useTranslation();
    const [downloading, setDownloading] = useState(false);

    const onDownload = async () => {
        if (downloading) return;
        setDownloading(true);
        try {
            await downloadFileFromUrl(url, filename);
        } catch {
            // downloadFileFromUrl already reports the failure
        } finally {
            setDownloading(false);
        }
    };

    return (
        <Tooltip content={`${t('Download')} ${filename}`} placement="top">
            <button
                type="button"
                onClick={onDownload}
                disabled={downloading}
                aria-label={`${t('Download')} ${filename}`}
                className="inline-flex items-center justify-center rounded-lg border border-divider bg-content1 p-3 text-default-500 hover:bg-default-100 disabled:opacity-60"
            >
                {downloading ? (
                    <Spinner size="sm" aria-label={t('Loading')} />
                ) : (
                    <Icon icon="mdi:file-download-outline" className="w-8 h-8" aria-hidden />
                )}
            </button>
        </Tooltip>
    );
};

/** Box-accordion icon slot for media items: renders the real image/video thumbnail. */
const BoxAccordionThumbnail: React.FC<{
    url?: string;
    isVideo: boolean;
    alt: string;
    isLoading: boolean;
    fallback: React.ReactNode;
}> = ({ url, isVideo, alt, isLoading, fallback }) => {
    const [failed, setFailed] = useState(false);

    if (!url) {
        if (isLoading) {
            return (
                <div className="flex items-center justify-center w-10 h-10 rounded-lg border border-default-200 bg-default-100 flex-shrink-0">
                    <Spinner size="sm" />
                </div>
            );
        }
        return <>{fallback}</>;
    }

    if (failed) return <>{fallback}</>;

    return (
        <div className="relative w-10 h-10 rounded-lg overflow-hidden border border-default-200 bg-default-100 flex-shrink-0">
            {isVideo ? (
                <>
                    <video
                        src={`${url}#t=0.1`}
                        muted
                        preload="metadata"
                        className="w-full h-full object-cover"
                        onError={() => setFailed(true)}
                    >
                        <track kind="captions" />
                    </video>
                    <div className="absolute inset-0 flex items-center justify-center bg-black/30">
                        <Icon icon="mdi:play" className="w-4 h-4 text-white" aria-hidden />
                    </div>
                </>
            ) : (
                <img
                    src={url}
                    alt={alt}
                    className="w-full h-full object-cover"
                    onError={() => setFailed(true)}
                />
            )}
        </div>
    );
};

const V2_TOP_LEVEL_ACCORDION_CLASS = 'gap-1 [&_hr]:hidden [&_.divider]:hidden';

/** Top-level primitive rows share the same splitted accordion bounds as line sections. */
const V2_TOP_LEVEL_FIELD_CLASS_NAMES = {
    ...LINE_ACCORDION_CLASS_NAMES,
    base: `${LINE_ACCORDION_CLASS_NAMES.base} !py-0 !mb-0 !shadow-none !rounded-none`,
    trigger: 'py-0 min-w-0 max-w-full w-full overflow-x-hidden items-center cursor-default pointer-events-none min-h-0 h-auto',
    content: 'hidden h-0 min-h-0 p-0 m-0 overflow-hidden',
} as const;

const V2_TOP_LEVEL_FIELD_ITEM_CLASS =
    'v2-top-level-field [&>h2>button]:block [&>h2>button>*]:!mb-0 [&>h2>button>*]:!py-0 [&_.renderer-key-value-block]:!mb-0 [&_.renderer-field-value]:!mb-0';

/** Renders ordered top-level accordion items (primitives interleaved with sections). */
export function renderV2TopLevelBlocks(
    elements: React.ReactElement[],
    accordionProps: {
        selectedKeys: Set<string>;
        onSelectionChange: (keys: 'all' | Set<React.Key>) => void;
    }
): React.ReactNode {
    if (elements.length === 0) return null;
    return (
        <Accordion
            variant="splitted"
            selectionMode="multiple"
            selectedKeys={accordionProps.selectedKeys}
            onSelectionChange={accordionProps.onSelectionChange}
            isCompact
            className={V2_TOP_LEVEL_ACCORDION_CLASS}
        >
            {elements}
        </Accordion>
    );
}

export type ClaimScoreActionOutputV2RendererProps = {
    data: ActionOutputV2Payload;
    /** When false, returns an empty list (for unconditional hook usage in parent). */
    enabled?: boolean;
    searchQuery?: string;
    activeFilter?: 'matches' | 'partial' | 'mismatches' | null;
    stopGatePath?: string[] | null;
    nestedSelectedKeys?: Set<string>;
    onNestedSelectedKeysChange?: (keys: Set<string>) => void;
    /** Modal timestamp for CK/forte/teal source data quote display. */
    timestamp?: string;
    /** When provided, V2 delegates source-info modal opens to the parent (legacy renderer reuses its own modal). */
    setSourceJsonModal?: (state: SourceJsonModalState | null) => void;
    /** Agent name, used only to apply Virtual Inspection field order and hiding. */
    agentName?: string;
    /** Render section bodies without the top-level expand/collapse header. Default stays accordion. */
    hideSectionHeaders?: boolean;
};

export type ClaimScoreActionOutputV2HookResult = {
    /** Ordered top-level accordion items (field rows + sections) per `order`. */
    blocks: React.ReactElement[];
    modal: React.ReactElement | null;
    sourceIds: V2SourceId[] | null;
    templateData: unknown;
};

const matchesScoreFilter = (
    score: unknown,
    filter: 'matches' | 'partial' | 'mismatches' | null
): boolean => {
    if (!filter) return true;
    if (score === undefined || score === null) return false;
    const numScore = Math.round(parseScore(score as number | string));
    if (filter === 'matches') return numScore >= 85;
    if (filter === 'partial') return numScore >= 60 && numScore < 85;
    if (filter === 'mismatches') return numScore < 60;
    return true;
};

const matchesStopGateSegment = (remaining: string[] | null, key: string): boolean =>
    !remaining || remaining.length === 0 || segmentMatchesKey(remaining[0], key);

const nextStopGateRemaining = (remaining: string[] | null): string[] | null =>
    remaining && remaining.length > 0 ? remaining.slice(1) : null;

/** A stop-gate segment that matches no child leaves the section unfiltered. */
const stopGateRemainingOrAll = (remaining: string[] | null, keys: string[]): string[] | null => {
    if (!remaining || remaining.length === 0) return remaining;
    const hasMatch = keys.some(
        (key) => !V2_IGNORED_NODE_KEYS.has(key) && key !== 'score' && matchesStopGateSegment(remaining, key)
    );
    return hasMatch ? remaining : null;
};

const resolveSectionHeaderScore = (
    value: unknown
): { score?: unknown; color: ScoreColorName | null } => {
    if (!value || typeof value !== 'object' || Array.isArray(value)) return { color: null };
    if (isScoredNode(value)) {
        const explanation = getScoredNodeExplanation(value);
        const header = resolveNodeHeaderScore(value, explanation);
        return { score: header.score, color: header.color };
    }
    return { score: (value as Record<string, unknown>).score, color: null };
};

const VIRTUAL_INSPECTION_HIDDEN_KEYS = new Set(['id', 'filename', 'mediatype', 'source_id', 'required', 'state']);
const VIRTUAL_INSPECTION_FIELD_ORDER = ['name', '@attachment', 'location'];

export const isVirtualInspectionAgentName = (name?: string): boolean =>
    !!name && name.replace(/_/g, ' ').toLowerCase().includes('virtual inspection');

const orderVirtualInspectionFields = (keys: string[]): string[] => {
    const visible = keys.filter((key) => !VIRTUAL_INSPECTION_HIDDEN_KEYS.has(stripFieldSuffix(key).toLowerCase()));
    const used = new Set<string>();
    const ordered: string[] = [];
    for (const preferred of VIRTUAL_INSPECTION_FIELD_ORDER) {
        const match = visible.find((key) => key.toLowerCase() === preferred);
        if (match && !used.has(match)) {
            ordered.push(match);
            used.add(match);
        }
    }
    for (const key of visible) {
        if (!used.has(key)) ordered.push(key);
    }
    return ordered;
};

const nodeMatchesSearch = (title: string, node: unknown, query: string): boolean => {
    if (!query.trim()) return true;
    const combined = `${title} ${collectSearchableText(node)}`;
    return textMatchesSearch(combined, query);
};

/**
 * Hook for V2 action output: ordered top-level accordion items, source metadata, and optional modal.
 */
export function useClaimScoreActionOutputV2TopLevelItems({
    data,
    enabled = true,
    searchQuery = '',
    activeFilter = null,
    stopGatePath = null,
    nestedSelectedKeys,
    onNestedSelectedKeysChange,
    timestamp,
    setSourceJsonModal: setSourceJsonModalProp,
    agentName,
    hideSectionHeaders = false,
}: ClaimScoreActionOutputV2RendererProps): ClaimScoreActionOutputV2HookResult {
    const { t } = useTranslation();
    const [internalNestedKeys, setInternalNestedKeys] = useState<Set<string>>(new Set());
    const [internalSourceJsonModal, setInternalSourceJsonModal] = useState<SourceJsonModalState | null>(null);

    const nestedKeys = nestedSelectedKeys ?? internalNestedKeys;
    const setNestedKeys = onNestedSelectedKeysChange ?? setInternalNestedKeys;
    const setSourceJsonModal = setSourceJsonModalProp ?? setInternalSourceJsonModal;
    const ownsModal = setSourceJsonModalProp == null;

    const topExplanation = enabled ? (data.explanation ?? {}) : {};
    const topOrder = enabled ? data.order : undefined;

    const attachmentSources = useMemo((): AttachmentSourcePayload[] => {
        if (!enabled) return [];
        const refs: AttachmentSourcePayload[] = [];
        const seen = new Set<string>();
        collectAttachmentSourcesFromValue(data, refs, seen);
        return refs;
    }, [enabled, data]);

    const {
        urlsBySourceId: attachmentUrlsBySourceId,
        isLoading: isLoadingAttachments,
        fetchFailed: attachmentFetchFailed,
    } = useV2AttachmentPresignedUrls(attachmentSources);

    const viewLabel = t('View');

    const passesSectionFilter = (score: unknown, title: string, node: unknown): boolean =>
        matchesScoreFilter(score, activeFilter) && nodeMatchesSearch(title, node, searchQuery);

    const handleNestedSelectionChange = useCallback(
        (keys: 'all' | Iterable<React.Key>) => {
            if (keys === 'all') {
                setNestedKeys(new Set());
                return;
            }
            setNestedKeys(new Set([...keys].map(String)));
        },
        [setNestedKeys]
    );

    const handleLineAccordionSelectionChange = useCallback((itemKey: string, keys: 'all' | Iterable<React.Key>) => {
        const next = new Set(nestedKeys);
        if (keys === 'all' || [...keys].length > 0) next.add(itemKey);
        else next.delete(itemKey);
        setNestedKeys(next);
    }, [nestedKeys, setNestedKeys]);

    const renderTableField = useCallback(
        (reactKey: string, displayKey: string, tableValue: { order?: string[]; table?: Record<string, unknown>[] }) => {
            const rows = Array.isArray(tableValue?.table) ? tableValue.table : [];
            if (rows.length === 0) {
                return renderKeyValueField(reactKey, formatSectionTitle(displayKey), '--');
            }
            const presentKeys = collectTablePresentKeys(rows);
            const columnsWithInfo = detectInfoColumns(presentKeys, rows);
            const orderedColumns = buildOrderedColumns(presentKeys, tableValue.order);
            const rowData = rows.map((row, idx) => ({ rowId: idx, ...row }));

            const wrapColumns = new Set(
                orderedColumns.filter((col) =>
                    rows.some((row) => {
                        const val = row[col];
                        return typeof val === 'string' && val.length > TABLE_WRAP_TEXT_MIN_LENGTH;
                    })
                )
            );

            const columnDefs: ColDef[] = orderedColumns.map((col) => {
                const colHasLongText = wrapColumns.has(col);
                return {
                    field: col,
                    headerName: formatSectionTitle(col),
                    flex: colHasLongText ? 2 : 1,
                    minWidth: colHasLongText ? 220 : 120,
                    cellClass: 'text-sm text-default-500',
                    cellRenderer: V2InfoCellRenderer,
                    ...(colHasLongText && { wrapText: true, autoHeight: true }),
                };
            });

            return (
                <div key={reactKey} className="py-2 border-b border-default-200 last:border-b-0 w-full">
                    <div className="renderer-field-name capitalize mb-2">{formatSectionTitle(displayKey)}</div>
                    <div className="overflow-x-auto rounded border border-default-200 max-w-full doc-csg-v2-grid-header-gray">
                        <style>{`
                          .doc-csg-v2-grid-header-gray .ag-header,
                          .doc-csg-v2-grid-header-gray .ag-header-cell {
                            background-color: rgb(229 231 235) !important;
                          }
                          .doc-csg-v2-grid-header-gray .ag-layout-auto-height .ag-body-viewport,
                          .doc-csg-v2-grid-header-gray .ag-layout-auto-height .ag-center-cols-viewport,
                          .doc-csg-v2-grid-header-gray .ag-layout-auto-height .ag-center-cols-container {
                            min-height: 0 !important;
                          }
                          .doc-csg-v2-grid-header-gray .ag-cell {
                            line-height: 1.4 !important;
                            padding-top: 8px !important;
                            padding-bottom: 8px !important;
                          }
                          .doc-csg-v2-grid-header-gray .ag-row {
                            border-bottom: 1px solid #e5e7eb !important;
                          }
                        `}</style>
                        <div className="ag-theme-alpine w-full min-w-full" style={{ width: '100%' }}>
                            <AgGridReact
                                rowData={rowData}
                                columnDefs={columnDefs}
                                defaultColDef={GRID_DEFAULT_COL_DEF}
                                context={{ setSourceJsonModal, columnsWithInfo, viewLabel, wrapColumns }}
                                domLayout="autoHeight"
                                pagination={false}
                                suppressPaginationPanel
                                headerHeight={40}
                                {...(wrapColumns.size === 0 && { rowHeight: 40 })}
                                getRowId={(p) => String((p.data as { rowId?: number }).rowId ?? 0)}
                            />
                        </div>
                    </div>
                </div>
            );
        },
        [setSourceJsonModal, viewLabel]
    );

    function renderLeafValue(value: unknown): React.ReactNode {
        if (Array.isArray(value)) {
            return (
                <div className="space-y-1">
                    {value.map((item, idx) => (
                        <div key={`leaf-${idx}-${typeof item === 'object' ? 'obj' : String(item)}`} className={LEAF_VALUE_CLASS}>
                            {typeof item === 'object' && item !== null ? '--' : renderLeafValue(item)}
                        </div>
                    ))}
                </div>
            );
        }
        let text = '--';
        if (typeof value === 'boolean') text = value ? 'True' : 'False';
        else if (typeof value === 'string' && value !== '') text = value;
        else if (typeof value === 'number') text = String(value);
        return <span className={LEAF_VALUE_CLASS}>{text}</span>;
    }

    function renderNestedBoxAccordion(children: React.ReactElement | React.ReactElement[]): React.ReactElement {
        return (
            <Accordion
                variant="light"
                isCompact
                selectionMode="multiple"
                selectedKeys={nestedKeys}
                onSelectionChange={handleNestedSelectionChange}
                className={V2_NESTED_LIGHT_ACCORDION_CLASS}
                dividerProps={HIDDEN_ACCORDION_DIVIDER_PROPS}
            >
                {children}
            </Accordion>
        );
    }

    function buildSectionBodyFromValue(
        sectionKey: string,
        value: unknown,
        fieldsKeyPrefix: string,
        arrayLabel: string,
        sectionLabelBase?: string,
        arrayKeyPrefix?: string
    ): React.ReactNode | null {
        if (Array.isArray(value)) {
            return renderArrayBodyContent(
                sectionKey,
                arrayLabel,
                value,
                arrayKeyPrefix ?? fieldsKeyPrefix
            );
        }
        const content = getV2SectionFieldRecord(value);
        if (!content) return null;
        const orderedKeys = resolveOrderedKeys(content.fields, content.order);
        return renderExplanationFields(
            content.fields,
            orderedKeys,
            fieldsKeyPrefix,
            sectionLabelBase ?? sectionKey
        );
    }

    function renderArraySectionItemBody(
        item: unknown,
        entryKey: string,
        idx: number,
        keyPrefix: string,
        itemKey: string
    ): React.ReactNode {
        if (isScoredNode(item)) {
            return renderSectionWithScore(`${entryKey}-${idx}`, item, `${keyPrefix}-${entryKey}`);
        }
        if (typeof item === 'object' && item !== null && !Array.isArray(item)) {
            const record = item as Record<string, unknown>;
            return (
                <div className={V2_NESTED_SECTION_CLASS}>
                    {renderExplanationFields(record, resolveOrderedKeys(record), itemKey)}
                </div>
            );
        }
        return renderLeafValue(item);
    }

    function renderArraySectionItems(
        items: unknown[],
        sectionLabel: string,
        keyPrefix: string,
        entryKey: string
    ): React.ReactNode {
        return items.map((item, idx) => {
            const itemLabel = `${sectionLabel} #${idx + 1}`;
            const itemKey = `${keyPrefix}-${entryKey}-${idx}`;
            return (
                <div key={itemKey} className="border-b border-default-200 pb-2 mb-2 last:border-b-0">
                    <div className="renderer-text-primary capitalize font-semibold mb-2">{itemLabel}</div>
                    {renderArraySectionItemBody(item, entryKey, idx, keyPrefix, itemKey)}
                </div>
            );
        });
    }

    /** Array body without repeating the section label (used inside box/line accordions). */
    function renderArrayBodyContent(
        entryKey: string,
        label: string,
        entryValue: unknown[],
        keyPrefix: string
    ): React.ReactNode {
        const isPrimitiveOnly = entryValue.every((item) => isV2PrimitiveValue(item));
        if (isPrimitiveOnly) {
            return (
                <div className="space-y-0.5">
                    {entryValue.map((item, idx) => (
                        <div key={`${entryKey}-prim-${idx}-${String(item)}`} className={LEAF_VALUE_CLASS}>
                            {item === null ? '--' : String(item)}
                        </div>
                    ))}
                </div>
            );
        }
        return renderArraySectionItems(entryValue, label, keyPrefix, entryKey);
    }

    /** Array-valued field: a flat list for primitive-only arrays, else nested per-item boxes. */
    function renderArrayField(
        fieldKey: string,
        entryKey: string,
        label: string,
        entryValue: unknown[],
        keyPrefix: string
    ): React.ReactNode {
        const isPrimitiveOnly = entryValue.every((item) => isV2PrimitiveValue(item));
        if (isPrimitiveOnly) {
            return renderKeyValueField(
                fieldKey,
                label,
                renderArrayBodyContent(entryKey, label, entryValue, keyPrefix)
            );
        }
        return (
            <div key={fieldKey} className="mb-2">
                <div className="renderer-field-name capitalize font-medium text-default-700 mb-2">
                    {label}
                </div>
                <div className={V2_NESTED_SECTION_CLASS}>
                    {renderArrayBodyContent(entryKey, label, entryValue, keyPrefix)}
                </div>
            </div>
        );
    }

    function renderExplanationFields(
        explanation: Record<string, unknown>,
        orderedKeys: string[],
        keyPrefix: string,
        sectionLabelBase?: string
    ): React.ReactNode {
        let numericChildIdx = 0;
        const keysToRender = isVirtualInspectionAgentName(agentName)
            ? orderVirtualInspectionFields(orderedKeys)
            : orderedKeys;
        return (
            <div className={V2_ACCORDION_CONTENT_CLASS}>
                {keysToRender.map((entryKey) => {
                    if (V2_IGNORED_NODE_KEYS.has(entryKey) || isV2ScoreFieldKey(entryKey)) return null;
                    const entryValue = explanation[entryKey];

                    const fieldFormat = getFieldFormat(entryKey);
                    if (Array.isArray(entryValue) && fieldFormat !== 'collapse' && fieldFormat !== 'attachment' && fieldFormat !== 'links') {
                        const sectionLabel = formatSectionTitle(entryKey);
                        return renderArrayField(`${keyPrefix}-${entryKey}-array`, entryKey, sectionLabel, entryValue, keyPrefix);
                    }

                    const mediaBox = tryRenderMediaAttachmentBoxAccordion(entryKey, entryValue, keyPrefix, {
                        parentSectionKey: sectionLabelBase,
                    });
                    if (mediaBox) return mediaBox;

                    if (fieldFormat) {
                        return renderV2Field(entryKey, entryValue, keyPrefix);
                    }

                    // Flat by default inside box / nested line; only @collapse creates a line accordion.
                    if (isScoredNode(entryValue)) {
                        const customTitle = isNumericExplanationKey(entryKey) && sectionLabelBase
                            ? `${formatSectionTitle(sectionLabelBase)} #${++numericChildIdx}`
                            : undefined;
                        return renderSectionWithScore(entryKey, entryValue, keyPrefix, customTitle);
                    }

                    return renderV2Field(entryKey, entryValue, keyPrefix);
                })}
            </div>
        );
    }

    function renderSectionWithScore(
        sectionKey: string,
        sectionData: V2ScoredNode,
        keyPrefix: string,
        displayTitle?: string
    ): React.ReactNode {
        const explanation = getScoredNodeExplanation(sectionData);
        const { score: headerScore, color: headerScoreColor } = resolveNodeHeaderScore(sectionData, explanation);
        const orderedKeys = resolveOrderedKeys(explanation, sectionData.order);

        return (
            <div key={`${keyPrefix}-${sectionKey}`} id={`${keyPrefix}-${sectionKey}`}>
                <div className="flex justify-between items-center mb-2 mt-2">
                    <div className="renderer-text-primary capitalize font-semibold">
                        {displayTitle ?? formatSectionTitle(sectionKey)}
                    </div>
                    {renderHeaderScoreChip(headerScore, headerScoreColor)}
                </div>
                <div className={V2_NESTED_SECTION_CLASS}>
                    {renderExplanationFields(explanation, orderedKeys, `${keyPrefix}-${sectionKey}`, sectionKey)}
                </div>
            </div>
        );
    }

    function renderLineAccordionForValue(
        sectionKey: string,
        sectionData: unknown,
        keyPrefix: string,
        options?: {
            displayTitle?: string;
            itemKey?: string;
            contentClassName?: string;
            bodyFieldsKeyPrefix?: string;
            bodySectionLabelBase?: string;
            arrayKeyPrefix?: string;
        }
    ): React.ReactNode {
        const itemKey = options?.itemKey ?? `${keyPrefix}-${sectionKey}-line`;
        const title = options?.displayTitle ?? resolveNodeTitle(sectionKey);
        const fieldsKeyPrefix = options?.bodyFieldsKeyPrefix ?? itemKey;
        const sectionLabelBase = options?.bodySectionLabelBase ?? sectionKey;
        const arrayKeyPrefix = options?.arrayKeyPrefix ?? keyPrefix;
        const body = buildSectionBodyFromValue(
            sectionKey,
            sectionData,
            fieldsKeyPrefix,
            title,
            sectionLabelBase,
            arrayKeyPrefix
        );
        if (body == null) return null;
        const { score, color } = resolveSectionHeaderScore(sectionData);
        return (
            <LineAccordionShell
                itemKey={itemKey}
                title={title}
                body={body}
                score={Array.isArray(sectionData) ? undefined : score}
                scoreColor={Array.isArray(sectionData) ? null : color}
                contentClassName={options?.contentClassName}
                selectedKeys={nestedKeys.has(itemKey) ? new Set([itemKey]) : new Set()}
                onSelectionChange={(keys) => handleLineAccordionSelectionChange(itemKey, keys)}
            />
        );
    }

    function renderAttachmentField(fieldKey: string, key: string, label: string, value: unknown): React.ReactNode {
        const items = normalizeAttachmentItems(value);
        if (items.length === 0) return null;
        const mediaHint = inferMediaTypeFromFieldKey(key);
        return (
            <div key={fieldKey} className="renderer-key-value-block mb-2">
                <div className="renderer-field-name capitalize font-medium text-default-700 mb-2">{label}</div>
                <div className="space-y-3">
                    {items.map((item) => {
                        const ext = item.file_extension ?? '';
                        const url = attachmentUrlsBySourceId[item.source_id];
                        const isHtml = ext === 'html';
                        const isVid = !isHtml && (mediaHint.isVideo || isVideoExtension(ext));
                        const isAud = !isHtml && (mediaHint.isAudio || isAudioExtension(ext));
                        return (
                            <div key={`${fieldKey}-${item.source_id}`}>
                                {url && isHtml ? (
                                    <HtmlAttachmentDownload url={url} filename={item.filename} />
                                ) : null}
                                {url && !isHtml ? (
                                    <AttachmentMediaWithFallback
                                        url={url}
                                        alt={item.filename}
                                        isVideo={isVid}
                                        isAudio={isAud}
                                    />
                                ) : null}
                                {attachmentFetchFailed && !url ? (
                                    <div className={`${ATTACHMENT_STATUS_BOX_CLASS} bg-content2 gap-2 text-default-500`}>
                                        <Icon icon="mdi:alert-circle-outline" className="w-8 h-8 text-default-400" aria-hidden />
                                        <p className="text-sm">{t('Attachment could not be loaded')}</p>
                                    </div>
                                ) : null}
                                {isLoadingAttachments && !url && !attachmentFetchFailed ? (
                                    <div className={ATTACHMENT_STATUS_BOX_CLASS}>
                                        <Spinner size="sm" aria-label={t('Loading')} />
                                    </div>
                                ) : null}
                                {item.description ? (
                                    <p className="mt-1 text-sm text-default-600 whitespace-pre-wrap">{item.description}</p>
                                ) : null}
                            </div>
                        );
                    })}
                </div>
            </div>
        );
    }

    function renderNestedObjectField(fieldKey: string, label: string, value: object): React.ReactNode {
        const content = getV2SectionFieldRecord(value);
        if (!content) return null;
        const nestedKeys = resolveOrderedKeys(content.fields, content.order).filter(
            (k) => !V2_IGNORED_NODE_KEYS.has(k) && k !== 'score'
        );
        if (nestedKeys.length === 0) return null;
        // Numeric-only object keys (e.g. part_id buckets): label as `${parent} #${idx+1}` instead of raw numeric key.
        const useParentIndexedLabel = nestedKeys.every((k) => isNumericExplanationKey(k));
        return (
            <div key={fieldKey} className="renderer-key-value-block mb-2">
                <div className="renderer-field-name capitalize font-medium text-default-700 mb-2">{label}</div>
                <div className={V2_NESTED_SECTION_CLASS}>
                    {nestedKeys.map((nestedKey, idx) => {
                        const labelOverride = useParentIndexedLabel ? `${label} #${idx + 1}` : undefined;
                        return renderV2Field(nestedKey, content.fields[nestedKey], fieldKey, labelOverride);
                    })}
                </div>
            </div>
        );
    }

    function renderFieldByFormat(
        format: string | null,
        fieldKey: string,
        key: string,
        label: string,
        value: unknown
    ): React.ReactNode | null {
        if (!format) return null;
        if (format === 'table' && value && typeof value === 'object' && !Array.isArray(value)) {
            return renderTableField(fieldKey, key, value);
        }
        if (format === 'collapse') {
            return renderLineAccordionForValue(key, value, fieldKey, {
                displayTitle: label,
                itemKey: fieldKey,
                bodyFieldsKeyPrefix: fieldKey,
                bodySectionLabelBase: key,
                contentClassName: 'pr-0 !py-0 !pl-0',
            });
        }
        const formatted = renderClaimScoreFormattedField(fieldKey, label, format, value);
        if (formatted) return formatted;
        if (format === 'attachment') {
            return renderAttachmentField(fieldKey, key, label, value);
        }
        return null;
    }

    function renderV2Field(key: string, value: unknown, keyPrefix: string, labelOverride?: string): React.ReactNode {
        if (V2_IGNORED_NODE_KEYS.has(key) || key === 'score') return null;

        const format = getFieldFormat(key);
        const fieldKey = `${keyPrefix}-${key}`;
        const label = labelOverride ?? resolveNodeTitle(key);

        const mediaBox = tryRenderMediaAttachmentBoxAccordion(key, value, keyPrefix);
        if (mediaBox) return mediaBox;

        const formatResult = renderFieldByFormat(format, fieldKey, key, label, value);
        if (format === 'links' || formatResult) return formatResult;

        if (isScoredNode(value)) {
            return renderSectionWithScore(key, value, keyPrefix, labelOverride);
        }
        if (stripFieldSuffix(key).toLowerCase() === 'score' && isScoreDisplayValue(value)) {
            return renderScoreRow(fieldKey, label, value, getScoreColorFromFormat(format));
        }
        if (Array.isArray(value)) {
            return renderArrayField(fieldKey, key, label, value, keyPrefix);
        }
        if (value && typeof value === 'object') {
            const geoPoint = readGeoPoint(value);
            if (geoPoint) {
                const record = value as Record<string, unknown>;
                const hideLocationAccuracy = stripFieldSuffix(key).toLowerCase() === 'location';
                const extraKeys = Object.keys(record).filter((entryKey) => {
                    if (isGeoAxisKey(entryKey) || V2_IGNORED_NODE_KEYS.has(entryKey) || entryKey === 'score') return false;
                    return !(hideLocationAccuracy && stripFieldSuffix(entryKey).toLowerCase() === 'accuracy');
                });
                return (
                    <div key={fieldKey} className="renderer-key-value-block mb-2">
                        <div className="renderer-field-name capitalize font-medium text-default-700 mb-1">{label}</div>
                        {renderMap({ modified_GPS: { latitude: geoPoint.lat, longitude: geoPoint.lng } })}
                        {extraKeys.map((entryKey) => renderV2Field(entryKey, record[entryKey], fieldKey))}
                    </div>
                );
            }
            return renderNestedObjectField(fieldKey, label, value);
        }

        return renderKeyValueField(fieldKey, label, renderLeafValue(value));
    }

    function renderBoxAccordion(
        sectionKey: string,
        sectionData: unknown,
        parentKey: string,
        displayTitleOverride?: string,
        iconSectionKey?: string
    ): React.ReactElement | null {
            const itemKey = `${parentKey}-box-${sectionKey}`;
            const iconMeta = pickBoxAccordionIcon(sectionKey, iconSectionKey);
            const isMediaParent = iconSectionKey === 'image_analysis' || iconSectionKey === 'video_analysis';
            const displayTitle = displayTitleOverride ??
                (isMediaSourceKey(sectionKey) || isMediaParent ? SliceSectionTitle(sectionKey) : formatSectionTitle(sectionKey));

            let body: React.ReactNode = null;
            let headerScore: unknown;
            let headerScoreColor: ScoreColorName | null = null;
            let attachmentScanTarget: unknown = sectionData;

            if (Array.isArray(sectionData)) {
                if (sectionData.length === 0) return null;
                body = renderArrayBodyContent(sectionKey, displayTitle, sectionData, itemKey);
            } else if (isScoredNode(sectionData)) {
                const explanation = getScoredNodeExplanation(sectionData);
                const orderedKeys = resolveOrderedKeys(explanation, sectionData.order);
                if (Object.keys(explanation).length === 0) return null;
                const header = resolveNodeHeaderScore(sectionData);
                headerScore = header.score;
                headerScoreColor = header.color;
                attachmentScanTarget = explanation;
                body = renderExplanationFields(explanation, orderedKeys, itemKey, sectionKey);
            } else if (sectionData && typeof sectionData === 'object') {
                const content = getV2SectionFieldRecord(sectionData);
                if (!content) return null;
                const orderedKeys = resolveOrderedKeys(content.fields, content.order).filter(
                    (k) => !V2_IGNORED_NODE_KEYS.has(k) && k !== 'score'
                );
                if (orderedKeys.length === 0) return null;
                const record = sectionData as Record<string, unknown>;
                headerScore = record.score;
                attachmentScanTarget = content.fields;
                body = renderExplanationFields(content.fields, orderedKeys, itemKey, sectionKey);
            } else {
                return null;
            }

            // actual image/video thumbnail (play overlay for video). Section boxes that merely contain
            const attachmentRefs: AttachmentSourcePayload[] = [];
            if (isMediaSourceKey(sectionKey)) {
                collectAttachmentSourcesFromValue(attachmentScanTarget, attachmentRefs, new Set());
            }
            const mediaRef = attachmentRefs[0];
            const mediaUrl = mediaRef ? attachmentUrlsBySourceId[mediaRef.source_id] : undefined;
            const isHtmlThumb = mediaRef?.file_extension === 'html';
            const isVideoThumb = !!mediaRef && !isHtmlThumb && isVideoExtension(mediaRef.file_extension ?? '');
            const staticIcon = (
                <div className={`p-2 shrink-0 ${iconMeta.bgColor} rounded-lg`}>
                    <Icon
                        icon={iconMeta.icon}
                        className={iconMeta.iconColor}
                        style={{ width: '20px', height: '20px', flexShrink: 0 }}
                    />
                </div>
            );
            const thumbnail = mediaRef && !isHtmlThumb ? (
                <BoxAccordionThumbnail
                    url={mediaUrl}
                    isVideo={isVideoThumb}
                    alt={mediaRef.filename}
                    isLoading={isLoadingAttachments}
                    fallback={staticIcon}
                />
            ) : (
                staticIcon
            );

            return (
                <AccordionItem
                    key={itemKey}
                    aria-label={displayTitle}
                    title={
                        <BoxAccordionTitleRow
                            displayTitle={displayTitle}
                            subtitle={iconMeta.subtitle}
                            thumbnail={thumbnail}
                            headerScore={headerScore}
                            headerScoreColor={headerScoreColor}
                            showScore={!Array.isArray(sectionData)}
                        />
                    }
                    classNames={BOX_ACCORDION_CLASS_NAMES}
                    className="mb-2"
                >
                    <div className={V2_ACCORDION_CONTENT_CLASS}>
                        {body}
                    </div>
                </AccordionItem>
            );
    }

    /** Box accordion for `filename__uuid` objects with an embedded `@attachment` field; ignores `@collapse`. */
    function tryRenderMediaAttachmentBoxAccordion(
        sectionKey: string,
        sectionData: unknown,
        keyPrefix: string,
        options?: { parentSectionKey?: string; displayTitle?: string; wrapNested?: boolean }
    ): React.ReactElement | null {
        if (!isMediaAttachmentObject(sectionKey, sectionData)) return null;
        const box = renderBoxAccordion(
            sectionKey,
            sectionData,
            keyPrefix,
            options?.displayTitle,
            options?.parentSectionKey
        );
        if (!box) return null;
        return options?.wrapNested === false ? box : renderNestedBoxAccordion(box);
    }

    function renderTopLevelSectionBody(
        node: V2TopLevelSectionValue,
        parentKey: string,
        parentSectionKey?: string,
        stopGateRemaining: string[] | null = null
    ): React.ReactNode {
            if (Array.isArray(node)) {
                return renderArrayBodyContent(parentSectionKey ?? 'items', formatSectionTitle(parentSectionKey ?? 'items'), node, parentKey);
            }

            const content = getV2SectionFieldRecord(node);
            if (!content) return null;
            const { fields: explanation, order } = content;
            const orderedKeys = resolveOrderedKeys(explanation, order);
            const activeStopGateRemaining = stopGateRemainingOrAll(stopGateRemaining, orderedKeys);

            if (hasOnlyNumericExplanationKeys(explanation)) {
                const slots = expandNumericKeySlots(explanation, order, parentSectionKey);
                const isRequestsSection = parentSectionKey === V2_REQUESTS_SECTION_KEY;

                if (isRequestsSection) {
                    return (
                        <Accordion
                            variant="splitted"
                            selectionMode="multiple"
                            selectedKeys={nestedKeys}
                            onSelectionChange={handleNestedSelectionChange}
                            isCompact
                            className={V2_SPLITTED_ACCORDION_CLASS}
                        >
                            {slots
                                .map((slot) => {
                                    const requestNode = explanation[slot.key];
                                    if (!isV2NonPrimitiveValue(requestNode)) return null;
                                    if (!matchesStopGateSegment(activeStopGateRemaining, slot.key)) return null;
                                    const slotKey = `${parentKey}-request-${slot.index}`;
                                    const titleText = slot.label;
                                    const requestScore = isScoredNode(requestNode) ? requestNode.score : undefined;
                                    if (!passesSectionFilter(requestScore, titleText, requestNode)) return null;
                                    return createLineAccordionItem(
                                        slotKey,
                                        titleText,
                                        <div className="pt-1">
                                            {renderTopLevelSectionBody(
                                                requestNode as V2TopLevelSectionValue,
                                                slotKey,
                                                parentSectionKey,
                                                nextStopGateRemaining(activeStopGateRemaining)
                                            )}
                                        </div>,
                                        requestScore
                                    );
                                })
                                .filter(isReactElement)}
                        </Accordion>
                    );
                }

                const boxes = slots
                    .map((slot) => {
                        const childNode = explanation[slot.key];
                        if (!isV2NonPrimitiveValue(childNode)) return null;
                        if (!matchesStopGateSegment(activeStopGateRemaining, slot.key)) return null;
                        const childScore = isScoredNode(childNode) ? childNode.score : undefined;
                        if (!passesSectionFilter(childScore, slot.label, childNode)) return null;
                        return renderBoxAccordion(slot.key, childNode, parentKey, slot.label, parentSectionKey);
                    })
                    .filter(isReactElement);
                if (boxes.length === 0) return null;
                return renderNestedBoxAccordion(boxes);
            }

            const childEntries = orderedKeys
                .filter((k) => !V2_IGNORED_NODE_KEYS.has(k) && k !== 'score')
                .map((k) => [k, explanation[k]] as [string, unknown]);

            const hasPrimitiveOrFormatChild = childEntries.some(([k, v]) => !isTopLevelBodyAccordionChild(k, v));

            if (!hasPrimitiveOrFormatChild && childEntries.length > 0) {
                const boxes = childEntries
                    .filter(([childKey]) => matchesStopGateSegment(activeStopGateRemaining, childKey))
                    .map(([childKey, childVal]) =>
                        renderBoxAccordion(childKey, childVal, parentKey, undefined, parentSectionKey)
                    )
                    .filter(isReactElement);
                if (boxes.length === 0) return null;
                return renderNestedBoxAccordion(boxes);
            }

            // Mixed: primitives / dedicated formats as-is; non-primitives as line accordions.
            return (
                <div className={V2_ACCORDION_CONTENT_CLASS}>
                    {childEntries.map(([childKey, childVal]) => {
                        if (!matchesStopGateSegment(activeStopGateRemaining, childKey)) return null;
                        if (isTopLevelBodyAccordionChild(childKey, childVal)) {
                            const mediaBox = tryRenderMediaAttachmentBoxAccordion(childKey, childVal, parentKey, {
                                parentSectionKey,
                            });
                            if (mediaBox) return mediaBox;
                            return renderLineAccordionForValue(childKey, childVal, parentKey);
                        }
                        return renderV2Field(childKey, childVal, parentKey);
                    })}
                </div>
            );
    }

    const stopGateTopSegment = stopGatePath && stopGatePath.length > 0 ? stopGatePath[0] : null;

    const topLevelOrderedEntries = useMemo(() => {
        const entries = buildV2TopLevelOrderedEntries(topExplanation, topOrder);
        if (stopGateTopSegment) {
            const matched = entries.filter(
                (entry) => entry.kind === 'section' && segmentMatchesKey(stopGateTopSegment, entry.sectionKey)
            );
            return matched.length > 0 ? matched : entries;
        }
        return entries.filter((entry) =>
            entry.kind === 'primitive'
                ? nodeMatchesSearch(formatSectionTitle(entry.key), entry.value, searchQuery)
                : passesSectionFilter(
                      isScoredNode(entry.sectionValue) ? entry.sectionValue.score : undefined,
                      entry.title,
                      entry.sectionValue
                  )
        );
    }, [topExplanation, topOrder, activeFilter, searchQuery, stopGateTopSegment]);

    const { sourceIds, templateData } = useMemo((): {
        sourceIds: V2SourceId[] | null;
        templateData: unknown;
    } => {
        const s = data.sources;
        if (!s || typeof s !== 'object') {
            return { sourceIds: null, templateData: null };
        }
        return {
            sourceIds: getV2SourcesKnowledgeIds(s),
            templateData: getV2SourcesTemplateData(s),
        };
    }, [data.sources]);

    const modal = ownsModal ? (
        <SourceJsonModal
            state={internalSourceJsonModal}
            onClose={() => setInternalSourceJsonModal(null)}
            timestamp={timestamp}
        />
    ) : null;

    if (!enabled) {
        return { blocks: [], modal, sourceIds: null, templateData: null };
    }

    const blocks: React.ReactElement[] = [];

    for (const entry of topLevelOrderedEntries) {
        if (entry.kind === 'primitive') {
            const field = renderV2Field(entry.key, entry.value, 'v2-top');
            if (field) {
                blocks.push(
                    <AccordionItem
                        key={`v2-top-field-${entry.key}`}
                        aria-label={resolveNodeTitle(entry.key)}
                        title={field}
                        indicator={<></>}
                        classNames={V2_TOP_LEVEL_FIELD_CLASS_NAMES}
                        className={V2_TOP_LEVEL_FIELD_ITEM_CLASS}
                    />
                );
            }
            continue;
        }

        const { itemKey, sectionValue, title, sectionKey } = entry;
        const mediaBox = tryRenderMediaAttachmentBoxAccordion(sectionKey, sectionValue, 'v2-top', {
            displayTitle: title,
            wrapNested: false,
        });
        if (mediaBox) {
            blocks.push(mediaBox);
            continue;
        }

        const body = renderTopLevelSectionBody(
            sectionValue,
            itemKey,
            sectionKey,
            stopGatePath && stopGatePath.length > 0 ? stopGatePath.slice(1) : null
        );
        if (hideSectionHeaders) {
            blocks.push(
                <div key={itemKey} className="w-full min-w-0">
                    {body}
                </div>
            );
            continue;
        }
        const content = getV2SectionFieldRecord(sectionValue);
        const hasFlatFields = content
            ? resolveOrderedKeys(content.fields, content.order).some(
                  (k) => !V2_IGNORED_NODE_KEYS.has(k) && !isV2NonPrimitiveValue(content.fields[k])
              )
            : Array.isArray(sectionValue);
        const useContentBorder = !hasNestedScoredChildren(sectionValue) || hasFlatFields;
        const header = Array.isArray(sectionValue)
            ? { score: undefined, color: null as ScoreColorName | null }
            : resolveSectionHeaderScore(sectionValue);

        blocks.push(
            createLineAccordionItem(
                itemKey,
                title,
                useContentBorder ? (
                    <div className="pt-1 border border-default-300 rounded-lg p-2 border-2">{body}</div>
                ) : (
                    <div className="pt-1">{body}</div>
                ),
                header.score,
                header.color
            )
        );
    }

    return { blocks, modal, sourceIds, templateData };
}

/** Standalone V2 tree (or a sliced subtree wrapped as a payload) without the full Claim Score chrome. */
export function ClaimScoreActionOutputV2View({
    data,
    timestamp,
    agentName,
    hideSectionHeaders = false,
}: {
    data: ActionOutputV2Payload;
    timestamp?: string;
    agentName?: string;
    hideSectionHeaders?: boolean;
}) {
    const [selectedKeys, setSelectedKeys] = useState<Set<string>>(
        () => new Set((data.order ?? []).map(String))
    );
    const { blocks, modal } = useClaimScoreActionOutputV2TopLevelItems({
        data,
        timestamp,
        agentName,
        hideSectionHeaders,
    });
    return (
        <>
            {hideSectionHeaders
                ? blocks
                : renderV2TopLevelBlocks(blocks, {
                      selectedKeys,
                      onSelectionChange: (keys) => setSelectedKeys(keys as Set<string>),
                  })}
            {modal}
        </>
    );
}
